/**
 * Bundle Add-ons — cart logic
 * ---------------------------
 * Finds any product form that contains a `[data-bundle-addons]` block and,
 * on submit, adds every SELECTED add-on to the cart as its own line item
 * with its own real SKU — in a separate, independent /cart/add.js call
 * that runs alongside (not instead of) the theme's own add-to-cart
 * handling.
 *
 * Why a separate call instead of intercepting the whole form: many themes
 * (including this one, which is Dawn-based) already bind their own submit
 * handler to the product form to add the main product, show loading
 * states, update the cart drawer, etc. Taking over that submit entirely
 * risks double-adding the main product or breaking the theme's own
 * error/loading UI. Firing a second, independent request for just the
 * add-ons is safe regardless of what the theme's own JS does, and every
 * item still lands in the same cart as its own line.
 *
 * Every add-on line item gets two properties:
 *   - "Bundled with"  → customer-visible, shows which product it came with
 *   - "_bundle_id"    → hidden from the customer (Shopify hides any
 *                        property whose name starts with "_"), but still
 *                        appears on the order in Shopify admin and via the
 *                        Admin/Orders API — so a bundle can always be
 *                        regrouped later even though each line is a
 *                        distinct SKU for Unleashed.
 *
 * Quantity: each option (checkbox row, or the dropdown once something is
 * picked) reveals a small +/- quantity stepper. Selecting/deselecting shows
 * or hides it and resets it back to 1, so a stray number never lingers
 * unseen. The chosen quantity is read straight off that stepper's number
 * input when the form is submitted.
 */
(function () {
  'use strict';
 
  function generateBundleId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'bundle-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }
 
  // Reads the quantity stepper's number input near `context` (a checkbox
  // row or a radio-card row) and returns a clamped, whole-number quantity.
  // Falls back to 1 if the stepper is hidden/missing/empty.
  function readQuantity(context) {
    var input = context.querySelector('[data-bundle-addon-qty]');
    if (!input) return 1;
    var value = parseInt(input.value, 10);
    if (!value || value < 1) value = 1;
    return value;
  }
 
  function collectSelectedAddons(form, mainProductTitle, bundleId) {
    var items = [];
    var groups = form.querySelectorAll('[data-bundle-addon-group]');
 
    groups.forEach(function (group) {
      var groupTitle = group.getAttribute('data-group-title') || '';
      var allowMultiple = group.getAttribute('data-allow-multiple') === 'true';
 
      if (allowMultiple) {
        group.querySelectorAll('input[type="checkbox"]:checked').forEach(function (checkbox) {
          var row = checkbox.closest('[data-bundle-addon-option]') || group;
          items.push({
            id: parseInt(checkbox.getAttribute('data-variant-id'), 10),
            quantity: readQuantity(row),
            properties: {
              'Bundled with': mainProductTitle,
              '_bundle_id': bundleId,
              '_bundle_group': groupTitle
            }
          });
        });
      } else {
        var checkedRadio = group.querySelector('input[type="radio"]:checked');
        if (checkedRadio && checkedRadio.value) {
          var row = checkedRadio.closest('[data-bundle-addon-option]') || group;
          items.push({
            id: parseInt(checkedRadio.getAttribute('data-variant-id'), 10),
            quantity: readQuantity(row),
            properties: {
              'Bundled with': mainProductTitle,
              '_bundle_id': bundleId,
              '_bundle_group': groupTitle
            }
          });
        }
      }
    });
 
    return items;
  }
 
  // Shows/hides and resets the quantity stepper that lives inside `row`
  // (a checkbox's option row, or the select's row) based on `shouldShow`.
  function toggleQtyWrap(row, shouldShow) {
    var wrap = row.querySelector('[data-bundle-addon-qty-wrap]');
    if (!wrap) return;
    wrap.hidden = !shouldShow;
    if (!shouldShow) {
      var input = wrap.querySelector('[data-bundle-addon-qty]');
      if (input) input.value = 1;
    }
  }
 
  function adjustQty(wrap, delta) {
    var input = wrap.querySelector('[data-bundle-addon-qty]');
    if (!input) return;
    var value = parseInt(input.value, 10);
    if (!value || value < 1) value = 1;
    value += delta;
    if (value < 1) value = 1;
    input.value = value;
  }
 
  // Wires up the show/hide-on-selection behaviour and the +/- buttons for
  // every bundle-addons block on the page. Runs once at init; new blocks
  // never appear without a full page load in this theme, so no
  // MutationObserver is needed.
  function bindQuantityControls(root) {
    // Checkbox rows: qty stepper appears once its own checkbox is ticked.
    root.querySelectorAll('[data-bundle-addon-checkbox]').forEach(function (checkbox) {
      var row = checkbox.closest('[data-bundle-addon-option]');
      if (!row) return;
      toggleQtyWrap(row, checkbox.checked);
      checkbox.addEventListener('change', function () {
        toggleQtyWrap(row, checkbox.checked);
      });
    });
 
    // Single-choice (radio) groups: only one option can be picked at a
    // time, so when any radio in the group changes, hide/reset every
    // row's stepper first, then reveal the one for the option now
    // checked (if it isn't "No thanks", which has an empty value).
    root.querySelectorAll('[data-bundle-addon-group][data-allow-multiple="false"]').forEach(function (group) {
      var radios = group.querySelectorAll('[data-bundle-addon-radio]');
      radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          group.querySelectorAll('[data-bundle-addon-option]').forEach(function (optionRow) {
            toggleQtyWrap(optionRow, false);
          });
          if (radio.checked && radio.value) {
            var row = radio.closest('[data-bundle-addon-option]');
            if (row) toggleQtyWrap(row, true);
          }
        });
      });
    });
 
    // +/- buttons, delegated from the root so one listener covers every
    // stepper in the block.
    root.addEventListener('click', function (event) {
      var decreaseBtn = event.target.closest('[data-bundle-addon-qty-decrease]');
      var increaseBtn = event.target.closest('[data-bundle-addon-qty-increase]');
      if (!decreaseBtn && !increaseBtn) return;
      // These are type="button" so they don't submit the form, but stop
      // propagation just in case a theme has its own delegated handlers.
      event.preventDefault();
      var wrap = (decreaseBtn || increaseBtn).closest('[data-bundle-addon-qty-wrap]');
      if (!wrap) return;
      adjustQty(wrap, decreaseBtn ? -1 : 1);
    });
 
    // Typing directly into the number input: keep it a positive whole
    // number on every change.
    root.querySelectorAll('[data-bundle-addon-qty]').forEach(function (input) {
      input.addEventListener('input', function () {
        var value = parseInt(input.value, 10);
        if (input.value !== '' && (!value || value < 1)) {
          input.value = 1;
        }
      });
    });
  }
 
  function showMessage(block, message, isError) {
    var el = block.querySelector('[data-bundle-addons-message]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-bundle-addons-message', '');
      block.appendChild(el);
    }
    el.className = isError ? 'bundle-addons__error' : 'bundle-addons__confirmation';
    el.textContent = message;
    el.hidden = false;
  }
 
  function clearMessage(block) {
    var el = block.querySelector('[data-bundle-addons-message]');
    if (el) el.hidden = true;
  }
 
  function handleSubmit(event) {
    var form = event.currentTarget;
    var block = form.querySelector('[data-bundle-addons]');
    if (!block) return;
 
    clearMessage(block);
 
    var mainProductTitle = document.title.split(' – ')[0] || document.title;
    var addonItems = collectSelectedAddons(form, mainProductTitle, generateBundleId());
 
    // Nothing selected — nothing to do. The theme's own handler takes care
    // of the main product exactly as it normally would.
    if (addonItems.length === 0) return;
 
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: addonItems })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function () {
        showMessage(block, 'Add-ons added to your cart.', false);
      })
      .catch(function (err) {
        var message =
          (err && err.description) ||
          (err && err.message) ||
          'One of the selected add-ons could not be added (it may be out of stock) — the rest of your order was not affected.';
        showMessage(block, message, true);
      });
  }
 
  function init() {
    var addonBlocks = document.querySelectorAll('[data-bundle-addons]');
    addonBlocks.forEach(function (block) {
      bindQuantityControls(block);
 
      var form = block.closest('form');
      if (!form || form.dataset.bundleAddonsBound === 'true') return;
      form.dataset.bundleAddonsBound = 'true';
      // Registered without { capture: true } and without preventDefault,
      // so it runs alongside whatever the theme's own submit handler does
      // — it never blocks or replaces the native add-to-cart flow.
      form.addEventListener('submit', handleSubmit);
    });
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
 